/**
 * Storage Service
 * 
 * This service provides a clean abstraction layer for all file storage operations.
 * All Firebase Storage-specific code is encapsulated here.
 * 
 * Migration Strategy:
 * - When migrating to Azure Blob Storage or another provider:
 *   1. Keep the same function signatures (inputs/outputs)
 *   2. Replace Firebase Storage calls with Azure/new provider equivalents
 *   3. Maintain the same URL structure where possible
 *   4. Components using this service won't need changes
 * 
 * Example Azure Blob Storage Migration:
 * - Replace ref() with BlobServiceClient container references
 * - Replace uploadBytesResumable with blockBlobClient.uploadData
 * - Replace getDownloadURL with blockBlobClient.url
 * - Map storage paths to blob container/folder structure
 */

import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { storage } from '../config/firebase';

/**
 * Upload audio file to storage
 * @param {Blob} audioBlob - Audio blob to upload
 * @param {string} userId - User ID for file path
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<string>} Download URL of uploaded file
 */
export const uploadAudioMessage = async (audioBlob, userId, onProgress = null) => {
  try {
    // Generate unique filename
    const timestamp = Date.now();
    const filename = `audio_${timestamp}.webm`;
    const storagePath = `voice-messages/${userId}/${filename}`;
    
    // Create storage reference
    const storageRef = ref(storage, storagePath);
    
    // Upload file
    const uploadTask = uploadBytesResumable(storageRef, audioBlob, {
      contentType: 'audio/webm',
    });
    
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Calculate progress percentage
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(Math.round(progress));
          }
        },
        (error) => {
          console.error('Upload error:', error);
          reject(new Error('Failed to upload audio file'));
        },
        async () => {
          // Upload completed, get download URL
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            reject(new Error('Failed to get download URL'));
          }
        }
      );
    });
  } catch (error) {
    console.error('Error uploading audio:', error);
    throw new Error('Failed to upload audio message');
  }
};

/**
 * Delete audio file from storage
 * @param {string} fileUrl - File URL to delete
 * @returns {Promise<void>}
 */
export const deleteAudioMessage = async (fileUrl) => {
  try {
    // Extract storage path from URL
    const storageRef = ref(storage, fileUrl);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting audio:', error);
    // Don't throw - file might already be deleted
  }
};

/**
 * Upload user avatar image
 * @param {File} imageFile - Image file to upload
 * @param {string} userId - User ID for file path
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<string>} Download URL of uploaded image
 */
export const uploadAvatar = async (imageFile, userId, onProgress = null) => {
  try {
    // Validate file type
    if (!imageFile.type.startsWith('image/')) {
      throw new Error('Invalid file type. Please upload an image.');
    }
    
    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (imageFile.size > maxSize) {
      throw new Error('File too large. Maximum size is 5MB.');
    }
    
    const filename = `avatar_${Date.now()}.${imageFile.name.split('.').pop()}`;
    const storagePath = `avatars/${userId}/${filename}`;
    
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, imageFile, {
      contentType: imageFile.type,
    });
    
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(Math.round(progress));
          }
        },
        (error) => {
          console.error('Upload error:', error);
          reject(new Error('Failed to upload avatar'));
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            reject(new Error('Failed to get download URL'));
          }
        }
      );
    });
  } catch (error) {
    console.error('Error uploading avatar:', error);
    throw error;
  }
};

/**
 * Get storage reference for a file path
 * @param {string} path - Storage path
 * @returns {Object} Storage reference
 */
export const getStorageRef = (path) => {
  return ref(storage, path);
};

/**
 * Get download URL for a storage reference
 * @param {string} path - Storage path
 * @returns {Promise<string>} Download URL
 */
export const getFileURL = async (path) => {
  try {
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error('Error getting file URL:', error);
    throw new Error('Failed to get file URL');
  }
};
